import { Account, Tag, Disease } from 'shared/types';
import { ACCOUNT_RECORD_TYPES, TAG_CATEGORIES } from 'shared/constants';
import { HELPFUL_LINKS, PatientAdvocacyGroup } from './organizations-interfaces';

interface MatchingTag {
    tagName: string;
    whoTheyServe: string;
}

const compareAlphabetically = (a = '', b = ''): number => a.localeCompare(b);

const getCuratedTagName = (currentDisease: Disease, tagName: string): string =>
    currentDisease.curatedTagNames?.[tagName] ||
    (currentDisease.diseaseCategoriesWithDescriptions || []).find((category: Tag) => category.name === tagName)?.nameCurated ||
    tagName;

const getMatchingDiseaseCategory = (account: Account, currentDisease: Disease): MatchingTag | undefined => {
    const matchingDiseaseCategory = (account.tagsDiseaseCategory || [])
        .filter((diseaseCategory: string) => (currentDisease.tagsDiseaseCategory || []).includes(diseaseCategory))
        .sort(compareAlphabetically)[0];

    if (!matchingDiseaseCategory) {
        return undefined;
    }

    return {
        tagName: matchingDiseaseCategory,
        whoTheyServe: getCuratedTagName(currentDisease, matchingDiseaseCategory),
    };
};

const getMatchingAccountTag = (account: Account, currentDisease: Disease): MatchingTag | undefined => {
    const matchingAccountTag = (account.tagsAccount || []).filter((tagAccount: string) => (currentDisease.tagsAccount || []).includes(tagAccount)).sort(compareAlphabetically)[0];

    if (!matchingAccountTag) {
        return undefined;
    }

    return {
        tagName: matchingAccountTag,
        whoTheyServe: getCuratedTagName(currentDisease, matchingAccountTag),
    };
};

const getMatchingTag = (account: Account, currentDisease: Disease): MatchingTag | undefined =>
    getMatchingDiseaseCategory(account, currentDisease) || getMatchingAccountTag(account, currentDisease);

const isDiseaseSpecificMatch = (account: Account, currentDisease: Disease): boolean =>
    (currentDisease.organizationSupported || []).some(organization => organization.name === account.name);

export const eligiblePatientAdvocacyGroupAccounts = (allAccounts: Account[]): Account[] =>
    allAccounts.filter(account => account.recordType === ACCOUNT_RECORD_TYPES.patientAdvocacyGroup);

export const hasDiseaseSpecificPatientAdvocacyGroup = (allAccounts: Account[], currentDisease: Disease): boolean =>
    eligiblePatientAdvocacyGroupAccounts(allAccounts).some(account => isDiseaseSpecificMatch(account, currentDisease));

const isRareUmbrellaMatch = (account: Account): boolean => (account.epidemiology || []).includes(TAG_CATEGORIES.rare);

const getNonUmbrellaCounts = (
    allAccounts: Account[],
    currentDisease: Disease,
    includeDiseaseSpecific: boolean,
    includeTagMatches: boolean,
): { diseaseSpecificCount: number; tagPatientAdvocacyGroupCount: number } => {
    let diseaseSpecificCount = 0;
    let tagPatientAdvocacyGroupCount = 0;

    allAccounts.forEach((account: Account) => {
        if (includeDiseaseSpecific && isDiseaseSpecificMatch(account, currentDisease)) {
            diseaseSpecificCount++;

            return;
        }

        if (includeTagMatches && getMatchingTag(account, currentDisease)) {
            tagPatientAdvocacyGroupCount++;
        }
    });

    return {
        diseaseSpecificCount,
        tagPatientAdvocacyGroupCount,
    };
};

const shouldIncludeRareUmbrellas = (allAccounts: Account[], currentDisease: Disease, includeDiseaseSpecific: boolean, includeTagMatches: boolean): boolean => {
    const { diseaseSpecificCount, tagPatientAdvocacyGroupCount } = getNonUmbrellaCounts(allAccounts, currentDisease, includeDiseaseSpecific, includeTagMatches);

    return diseaseSpecificCount < 1 && tagPatientAdvocacyGroupCount < 2;
};

export const comparePatientAdvocacyGroups = (a: PatientAdvocacyGroup, b: PatientAdvocacyGroup): number => {
    const serveOrderDifference = a.serveOrder - b.serveOrder;

    if (serveOrderDifference !== 0) {
        return serveOrderDifference;
    }

    if (a.serveOrder === 2) {
        const whoTheyServeDifference = compareAlphabetically(a.whoTheyServe, b.whoTheyServe);

        if (whoTheyServeDifference !== 0) {
            return whoTheyServeDifference;
        }
    }

    const helpfulLinkDifference = a.linkOrder - b.linkOrder;

    if (helpfulLinkDifference !== 0) {
        return helpfulLinkDifference;
    }

    return compareAlphabetically(a.name, b.name);
};

export const availablePatientAdvocacyGroups = (
    allAccounts: Account[],
    currentDisease: Disease,
    includeDiseaseSpecific: boolean,
    includeTagMatches: boolean,
    includeRare: boolean,
) => {
    const includeRareUmbrellas = includeRare && shouldIncludeRareUmbrellas(allAccounts, currentDisease, includeDiseaseSpecific, includeTagMatches);

    return allAccounts
        .map((account: Account) => {
            const { id, expertDirectoryUrl, patientRegistryUrl, country, website } = account;
            const helpfulLinks: string[] = [];
            if (expertDirectoryUrl) {
                helpfulLinks.push(HELPFUL_LINKS.listOfExperts);
            }
            if (patientRegistryUrl) {
                helpfulLinks.push(HELPFUL_LINKS.researchRegistry);
            }
            const linkOrder = -1 * helpfulLinks.length;

            const matchingTag = includeTagMatches ? getMatchingTag(account, currentDisease) : undefined;

            // Disease Specific
            if (includeDiseaseSpecific) {
                if (isDiseaseSpecificMatch(account, currentDisease)) {
                    return {
                        id,
                        name: account.name,
                        url: website,
                        helpfulLinks,
                        expertDirectoryUrl,
                        patientRegistryUrl,
                        country,
                        linkOrder,
                        whoTheyServe: currentDisease.name,
                        serveOrder: 1,
                    } as PatientAdvocacyGroup;
                }
            }

            // Has disease or account tag matching with current disease
            if (matchingTag) {
                return {
                    id,
                    name: account.name,
                    url: website,
                    helpfulLinks,
                    expertDirectoryUrl,
                    patientRegistryUrl,
                    country,
                    linkOrder,
                    whoTheyServe: matchingTag.whoTheyServe,
                    serveOrder: 2,
                    tagName: matchingTag.tagName,
                } as PatientAdvocacyGroup;
            }

            // Rare tag is present
            if (includeRareUmbrellas) {
                if (isRareUmbrellaMatch(account)) {
                    return {
                        id,
                        name: account.name,
                        url: website,
                        helpfulLinks,
                        expertDirectoryUrl,
                        patientRegistryUrl,
                        country,
                        linkOrder,
                        whoTheyServe: 'Rare Diseases',
                        serveOrder: 3,
                    } as PatientAdvocacyGroup;
                }
            }

            // Null if none of the criteria is met
            return null;
        })
        .filter((acct): acct is PatientAdvocacyGroup => !!acct) // Filter out null values
        .sort(comparePatientAdvocacyGroups);
};
